"use client";

import { ListSkeleton, DetailSkeleton } from "@/components/ui/skeleton";
import { CodeBlock, CodeBlockCopyButton } from "@/components/ai-elements/code-block";
import {
  FileTree,
  FileTreeFile,
  FileTreeFolder,
} from "@/components/ai-elements/file-tree";
import { MarkdownPreviewToolbar } from "@/components/markdown-preview";
import { Button } from "@/components/ui/button";
import { AttachFileIcon } from "@/components/ui/attach-file";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DownloadIcon } from "@/components/ui/download";
import { Maximize2Icon } from "@/components/ui/maximize-2";
import { PanelLeftOpenIcon } from "@/components/ui/panel-left-open";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import { Spinner } from "@/components/ui/spinner";
import { TimerIcon } from "@/components/ui/timer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { extOf, LANGUAGE_BY_EXT, languageForPath } from "@/lib/file-kinds";
import { containerDisplayPath } from "@/types/container-files";
import type { ContainerFile, ContainerStatus } from "@/types/container-files";
import {
  DatabaseIcon,
  FileCode2Icon,
  FileIcon,
  FileImageIcon,
  FileJsonIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FolderIcon,
  FolderOpenIcon,
  LinkIcon,
  PanelLeftCloseIcon,
  TerminalIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { toast } from "sonner";

// ── Types ────────────────────────────────────────────────────────────────────

export type ContainerFilesPanelProps = {
  provider?: "anthropic" | "openai";
  files: ContainerFile[];
  status: ContainerStatus;
  loading: boolean;
  error?: string | null;
  /**
   * Resolves the file's text. Called when a file is opened and again after a
   * refresh reports it changed; results are cached per path *and version*.
   */
  onReadFile: (path: string) => Promise<string>;
  onRefresh?: () => void | Promise<void>;
  /**
   * ISO timestamp of when `files` was last read off the container. Shown so a
   * stale listing is never mistaken for a live one. Omit only if the list is
   * genuinely live.
   */
  listedAt?: string | null;
  /** Earliest provider/app deadline after which this sandbox is not reused. */
  usableUntil?: string | null;
  /**
   * Wakes a checkpointed container. Anthropic restores on any request that
   * references the container id, so this is usually the same call as
   * `onRefresh` — it is a separate prop because the user's intent differs, and
   * because it should reject rather than silently start a fresh sandbox.
   */
  onRestore?: () => Promise<void>;
  /**
   * Attaches a container file to the chat composer as a normal file
   * attachment. The container is not durable — it checkpoints when idle and is
   * gone 30 days after it was created — so this is how a file survives the
   * sandbox it was made in: attach it, and the next turn uploads it back into
   * whatever container is live then. Omit to hide the control.
   */
  onAttachFile?: (file: AttachableContainerFile) => void | Promise<void>;
  /** Downloads a copy of the selected file to the user's device. */
  onDownloadFile?: (file: AttachableContainerFile) => void | Promise<void>;
  /** Dismisses the panel. Renders a close control in the header when given. */
  onClose?: () => void;
  /**
   * Fires whenever the open file changes (undefined when none). The panel is
   * narrow until a file is picked, so its container needs this to widen itself
   * for the preview column.
   */
  onSelectedPathChange?: (path: string | undefined) => void;
  className?: string;
};

/** A container file lifted out as a chat attachment. */
export type AttachableContainerFile = {
  /** Path inside the container, e.g. "tmp/report.lsp". */
  path: string;
  /** Basename used as the attachment filename. */
  name: string;
  /** Full text content, already read off the container. */
  content: string;
};

type TreeNode = {
  path: string;
  name: string;
  kind: "folder" | "file";
  file?: ContainerFile;
  children: TreeNode[];
};

// ── Helpers ──────────────────────────────────────────────────────────────────

// NOTE: duplicated from files-panel.tsx. Lift both to a shared module rather
// than keeping a third copy.
const formatBytes = (value?: number) => {
  if (!value || value <= 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const formatTime = (value?: string | null) => {
  if (!value) return "–";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "–";
  return new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(parsed);
};

/**
 * Cache key for one file's text. Size and mtime are part of it, so a refresh
 * that reports the same path with new contents invalidates the copy the viewer
 * is holding: keying on the path alone meant a file the sandbox rewrote kept
 * showing its first version for as long as the panel stayed open.
 */
const versionKey = (file: ContainerFile) =>
  `${file.path}@${file.size ?? "?"}:${file.modifiedAt ?? "?"}`;

/**
 * Folds flat container paths into a sorted folder/file tree, keyed by display
 * path so the tree opens on `workspace/` and `input/` instead of burying both
 * under a `files/` node that every row shares.
 */
const buildTree = (files: ContainerFile[]): TreeNode[] => {
  const root: TreeNode[] = [];

  for (const file of files) {
    const segments = containerDisplayPath(file.path).split("/").filter(Boolean);
    if (segments.length === 0) continue;

    let level = root;
    let prefix = "";

    segments.forEach((segment, i) => {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      const isLeaf = i === segments.length - 1;
      let node = level.find((n) => n.name === segment && n.kind === (isLeaf ? "file" : "folder"));

      if (!node) {
        node = {
          path: prefix,
          name: segment,
          kind: isLeaf ? "file" : "folder",
          ...(isLeaf ? { file } : {}),
          children: [],
        };
        level.push(node);
      }
      level = node.children;
    });
  }

  const sort = (nodes: TreeNode[]): TreeNode[] => {
    nodes.sort((a, b) =>
      a.kind === b.kind
        ? a.name.localeCompare(b.name, "en", { numeric: true, sensitivity: "base" })
        : a.kind === "folder"
          ? -1
          : 1
    );
    for (const node of nodes) sort(node.children);
    return nodes;
  };

  return sort(root);
};

const collectFolderPaths = (nodes: TreeNode[], acc = new Set<string>()): Set<string> => {
  for (const node of nodes) {
    if (node.kind === "folder") {
      acc.add(node.path);
      collectFolderPaths(node.children, acc);
    }
  }
  return acc;
};

/** Short status word — the full sentence lives in the pill's tooltip. */
const STATUS_LABEL: Record<ContainerStatus, string> = {
  active: "Live",
  checkpointed: "Idle",
  expired: "Expired",
  none: "Off",
};

const STATUS_DETAIL: Record<ContainerStatus, string> = {
  active: "Sandbox active",
  checkpointed: "Sandbox idle — it wakes on the next request",
  expired: "Sandbox expired — its files were discarded",
  none: "No sandbox yet",
};

// The pill itself stays neutral and only the dot carries colour — a full
// tinted pill shouted louder than anything else in the panel.
const STATUS_DOT: Record<ContainerStatus, string> = {
  active: "bg-green-500",
  checkpointed: "bg-amber-500",
  expired: "bg-destructive",
  none: "bg-muted-foreground/50",
};

const StatusPill = ({ status }: { status: ContainerStatus }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-muted/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
        <span
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            STATUS_DOT[status],
            status === "active" && "animate-pulse"
          )}
        />
        {STATUS_LABEL[status]}
      </span>
    </TooltipTrigger>
    <TooltipContent side="bottom">{STATUS_DETAIL[status]}</TooltipContent>
  </Tooltip>
);

/**
 * Coarse "time left" wording. The sandbox lives 30 days, so minutes only
 * matter in the final hour — anything longer reads better in days or hours.
 */
const formatRemaining = (ms: number): { label: string; spoken: string } => {
  const minutes = Math.ceil(ms / 60_000);
  if (minutes <= 0) return { label: "Ending now", spoken: "are expiring now" };
  if (minutes < 60) {
    return { label: `${minutes} min left`, spoken: `expire in ${minutes} minutes` };
  }
  const hours = Math.ceil(minutes / 60);
  if (hours < 24) {
    return {
      label: `${hours} hr${hours === 1 ? "" : "s"} left`,
      spoken: `expire in ${hours} hour${hours === 1 ? "" : "s"}`,
    };
  }
  const days = Math.ceil(hours / 24);
  return {
    label: `${days} day${days === 1 ? "" : "s"} left`,
    spoken: `expire in ${days} day${days === 1 ? "" : "s"}`,
  };
};

const ExpiryCountdown = ({ usableUntil, provider }: { usableUntil: string; provider: "anthropic" | "openai" }) => {
  const deadline = Date.parse(usableUntil);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(intervalId);
  }, []);

  if (Number.isNaN(deadline)) return null;

  const remainingMs = Math.max(0, deadline - now);
  // Urgent only inside the last hour — a 30-day window spends most of its life
  // nowhere near the deadline.
  const urgent = remainingMs <= (provider === "openai" ? 5 : 60) * 60_000;
  const { label, spoken } = formatRemaining(remainingMs);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={`Sandbox files ${spoken}`}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
            urgent
              ? "bg-destructive/10 text-destructive"
              : "bg-muted/70 text-muted-foreground"
          )}
        >
          <TimerIcon size={11} />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64" side="bottom">
        {provider === "openai"
          ? "OpenAI deletes the sandbox and its files after 20 minutes of inactivity. Using the sandbox or browsing its files refreshes this timer. Download files you want to keep."
          : "The sandbox and its files are kept for 30 days after it is created, then it can no longer be reused. Gaps between turns cost nothing — it wakes again on the next code-execution turn. Attach or download files you want to keep before the 30 days are up."}
      </TooltipContent>
    </Tooltip>
  );
};

/** Low-emphasis icon action used across both panel headers. */
const IconAction = ({
  label,
  onClick,
  disabled,
  busy,
  compact = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  compact?: boolean;
  children: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn(
          "text-muted-foreground hover:text-foreground",
          compact ? "size-5 rounded-md" : "size-7 rounded-full"
        )}
        disabled={disabled}
        onClick={onClick}
      >
        {busy ? <Spinner className="size-3.5" /> : children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/** Extension-aware leaf icon — shape alone, so the tree stays monochrome. */
const FILE_ICON_BY_EXT: Record<string, typeof FileIcon> = {
  csv: FileSpreadsheetIcon,
  tsv: FileSpreadsheetIcon,
  xls: FileSpreadsheetIcon,
  xlsx: FileSpreadsheetIcon,
  json: FileJsonIcon,
  md: FileTextIcon,
  mdx: FileTextIcon,
  txt: FileTextIcon,
  log: FileTextIcon,
  sh: TerminalIcon,
  bash: TerminalIcon,
  sql: DatabaseIcon,
  png: FileImageIcon,
  jpg: FileImageIcon,
  jpeg: FileImageIcon,
  gif: FileImageIcon,
  svg: FileImageIcon,
  pdf: FileTextIcon,
};

const iconForFile = (name: string) => {
  const ext = extOf(name);
  if (FILE_ICON_BY_EXT[ext]) return FILE_ICON_BY_EXT[ext];
  return LANGUAGE_BY_EXT[ext] ? FileCode2Icon : FileIcon;
};

/** Panel width with only the tree showing, and once a preview opens beside it. */
const TREE_ONLY_WIDTH = 320;
const WITH_PREVIEW_WIDTH = 760;
/** Drag floors — the second keeps the preview column from collapsing to a sliver. */
const MIN_WIDTH = 240;
const MIN_WIDTH_WITH_PREVIEW = 420;

// ── Panel ────────────────────────────────────────────────────────────────────

export function ContainerFilesPanel({
  provider = "anthropic",
  files,
  status,
  loading,
  error,
  onReadFile,
  onRefresh,
  listedAt,
  usableUntil,
  onRestore,
  onAttachFile,
  onDownloadFile,
  onClose,
  onSelectedPathChange,
  className,
}: ContainerFilesPanelProps) {
  const [rawSelectedPath, setSelectedPath] = useState<string>();
  const [expandedOverride, setExpanded] = useState<Set<string>>();
  const [contentCache, setContentCache] = useState<Map<string, string>>(new Map());
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [explorerCollapsed, setExplorerCollapsed] = useState(false);

  const tree = useMemo(() => buildTree(files), [files]);

  // Expand every folder until the user collapses one, so a shallow container
  // reads at a glance. Derived rather than seeded via an effect.
  const defaultExpanded = useMemo(() => collectFolderPaths(tree), [tree]);
  const expanded = expandedOverride ?? defaultExpanded;

  // A dead container's file list is meaningless, so nothing stays selected
  // through an expiry. Derived so the state can't outlive the sandbox.
  const isLive = status === "active" || status === "checkpointed";
  const selectedPath = isLive ? rawSelectedPath : undefined;

  // Reported rather than lifted: the panel still owns selection, its container
  // only needs to know whether a preview column is on screen.
  useEffect(() => {
    onSelectedPathChange?.(selectedPath);
  }, [onSelectedPathChange, selectedPath]);

  const selectedFile = useMemo(
    () => files.find((f) => containerDisplayPath(f.path) === selectedPath),
    [files, selectedPath]
  );

  // Keyed by version rather than path, so the entry a refresh invalidates is
  // simply absent and the effect below fetches the new one.
  // OpenAI reports creation time rather than modification time. Re-read after
  // each listing even when an edit kept the file's path and byte length.
  const selectedKey = selectedFile
    ? `${versionKey(selectedFile)}${provider === "openai" ? `:${listedAt}` : ""}`
    : undefined;
  const selectedContent = selectedKey ? contentCache.get(selectedKey) : undefined;

  // Reads whatever the selection currently points at — a file the user just
  // clicked, or the open one after a refresh reported it changed. Driven by an
  // effect rather than by the click handler so both cases go through one path.
  useEffect(() => {
    if (!selectedFile || !selectedKey || contentCache.has(selectedKey)) return;

    // Selecting a second file before the first resolves must not let the
    // slower read clear the spinner or post an error over the newer one.
    let cancelled = false;
    setReading(true);
    setReadError(null);

    void onReadFile(selectedFile.path)
      .then((text) => {
        if (cancelled) return;
        setContentCache((prev) => new Map(prev).set(selectedKey, text));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReadError(err instanceof Error ? err.message : "Failed to read file.");
      })
      .finally(() => {
        if (!cancelled) setReading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contentCache, onReadFile, selectedFile, selectedKey]);

  const handleSelect = useCallback(
    (path: string) => {
      // Tree keys are display paths (the shared /files/ root removed) so the
      // folders nest sensibly, but the container only answers to absolute
      // paths — the read above goes by the file's original path, not this key.
      const file = files.find((f) => containerDisplayPath(f.path) === path);
      if (!file) return; // folder rows toggle; only files open

      setReadError(null);
      setPreviewExpanded(false);
      setSelectedPath(path);
    },
    [files]
  );

  const handleRestore = useCallback(async () => {
    if (!onRestore) return;
    setRestoring(true);
    try {
      await onRestore();
    } catch (err: unknown) {
      console.error("[ContainerFilesPanel] restore failed:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to restore the sandbox."
      );
    } finally {
      setRestoring(false);
    }
  }, [onRestore]);

  const handleAttach = useCallback(async () => {
    if (!onAttachFile || !selectedPath) return;
    // Only ever attaches content already read off the container — never a
    // re-read, so what is attached is exactly what the viewer is showing.
    if (selectedContent === undefined) return;

    setAttaching(true);
    try {
      await onAttachFile({
        path: selectedFile?.path ?? selectedPath,
        name: selectedPath.split("/").pop() || selectedPath,
        content: selectedContent,
      });
      toast.success("Attached to the chat.");
    } catch (err: unknown) {
      console.error("[ContainerFilesPanel] attach failed:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to attach the file."
      );
    } finally {
      setAttaching(false);
    }
  }, [onAttachFile, selectedContent, selectedFile, selectedPath]);

  const handleDownload = useCallback(async () => {
    if (!onDownloadFile || !selectedPath) return;
    if (selectedContent === undefined) return;

    setDownloading(true);
    try {
      await onDownloadFile({
        path: selectedFile?.path ?? selectedPath,
        name: selectedPath.split("/").pop() || selectedPath,
        content: selectedContent,
      });
    } catch (err: unknown) {
      console.error("[ContainerFilesPanel] download failed:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to download the file."
      );
    } finally {
      setDownloading(false);
    }
  }, [onDownloadFile, selectedContent, selectedFile, selectedPath]);

  const handleCopyPath = useCallback(async () => {
    if (!selectedFile) return;

    try {
      await navigator.clipboard.writeText(selectedFile.path);
      toast.success("File path copied.");
    } catch (err: unknown) {
      console.error("[ContainerFilesPanel] copy path failed:", err);
      toast.error("Failed to copy the file path.");
    }
  }, [selectedFile]);

  const renderNode = (node: TreeNode): ReactNode => {
    if (node.kind === "folder") {
      return (
        <FileTreeFolder
          key={node.path}
          path={node.path}
          name={node.name}
          className="[&>div:first-child]:rounded-md [&>div:first-child]:px-1.5 [&>div:first-child]:py-1 [&>div:first-child]:hover:bg-accent/60"
          icon={<FolderIcon className="size-3.5 text-muted-foreground" />}
          openIcon={<FolderOpenIcon className="size-3.5 text-muted-foreground" />}
        >
          {node.children.map(renderNode)}
        </FileTreeFolder>
      );
    }
    const Icon = iconForFile(node.name);
    return (
      <FileTreeFile
        key={node.path}
        path={node.path}
        name={node.name}
        icon={<Icon className="size-3.5 text-muted-foreground" />}
        className="rounded-md px-1.5 py-1 hover:bg-accent/60 aria-selected:bg-accent aria-selected:text-accent-foreground aria-selected:[&_svg]:text-accent-foreground"
      />
    );
  };

  const treeNode = (
    <div className="h-full overflow-y-auto px-2 py-1.5 scrollbar-minimal">
      {loading && files.length === 0 ? (
        <ListSkeleton rows={4} compact />
      ) : tree.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
          <span className="flex size-9 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <FolderIcon className="size-4" />
          </span>
          <p className="text-xs text-muted-foreground">
            {status === "expired"
              ? "The sandbox expired and its files were discarded."
              : "Nothing here yet — files the sandbox writes show up as they appear."}
          </p>
        </div>
      ) : (
        <FileTree
          className="rounded-none border-0 bg-transparent text-[13px] [&>div]:p-0"
          expanded={expanded}
          onExpandedChange={setExpanded}
          selectedPath={selectedPath}
          onSelect={handleSelect}
        >
          {tree.map(renderNode)}
        </FileTree>
      )}
    </div>
  );

  const fileCount = files.length;
  const selectedName = selectedPath?.split("/").pop() ?? "";
  const selectedDir = selectedPath?.slice(0, selectedPath.length - selectedName.length) ?? "";

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden bg-background",
        className
      )}
    >
      {/* Header — title and status on top, everything countable in the micro
          line beneath, so the row never wraps into a second control row. */}
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border/60 px-3 py-2.5">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium tracking-tight text-foreground">
              Sandbox files
            </span>
            <StatusPill status={status} />
            {usableUntil && (status === "active" || status === "checkpointed") ? (
              <ExpiryCountdown key={usableUntil} usableUntil={usableUntil} provider={provider} />
            ) : null}
          </div>
          <p className="truncate text-[11px] text-muted-foreground">
            {status === "expired"
              ? "Files discarded"
              : [
                  `${fileCount} ${fileCount === 1 ? "file" : "files"}`,
                  // A list with no time reads as a live one, and it will not always be.
                  listedAt ? `as of ${formatTime(listedAt)}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {status === "checkpointed" && onRestore ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-2.5 text-xs"
              onClick={() => void handleRestore()}
              disabled={restoring}
            >
              {restoring ? <Spinner className="size-3.5" /> : <RefreshCWIcon size={14} />}
              {restoring ? "Restoring…" : "Restore"}
            </Button>
          ) : onRefresh ? (
            <IconAction
              label="Refresh"
              busy={loading}
              disabled={loading || status === "expired"}
              onClick={() => void onRefresh()}
            >
              <RefreshCWIcon size={14} />
            </IconAction>
          ) : null}
          {selectedPath ? (
            <IconAction
              label={explorerCollapsed ? "Expand file explorer" : "Collapse file explorer"}
              onClick={() => setExplorerCollapsed((collapsed) => !collapsed)}
            >
              {explorerCollapsed ? (
                <PanelLeftOpenIcon size={14} />
              ) : (
                <PanelLeftCloseIcon className="size-3.5" />
              )}
            </IconAction>
          ) : null}
          {onClose ? (
            <IconAction label="Close sandbox files" onClick={onClose}>
              <XIcon className="size-3.5" />
            </IconAction>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="shrink-0 border-b border-border/60 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}

      {/* Body: tree + viewer */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* The tree owns the whole panel until a file is opened, so a narrow
            panel is still readable; the preview then takes the space the user
            drags open beside it. */}
        <div
          className={cn(
            "flex min-h-0 shrink-0 flex-col transition-[width] duration-200",
            selectedPath
              ? explorerCollapsed
                ? "w-0 overflow-hidden"
                : "w-56 border-r border-border/60 bg-muted/20"
              : "min-w-0 flex-1"
          )}
        >
          {!selectedPath || !explorerCollapsed ? (
            <div className="min-h-0 flex-1">{treeNode}</div>
          ) : null}
        </div>

        {selectedPath ? (
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {reading ? (
              <DetailSkeleton />
            ) : readError ? (
              <div className="flex flex-1 items-center justify-center px-6">
                <p className="text-xs text-destructive">{readError}</p>
              </div>
            ) : (
              <>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <CodeBlock
                    className="flex h-full min-h-0 flex-col rounded-none border-0 bg-transparent"
                    code={selectedContent ?? ""}
                    contentClassName="pt-14 text-xs [&_code]:text-xs"
                    language={languageForPath(selectedPath)}
                    scrollClassName="min-h-0 min-w-0 flex-1 overflow-auto scrollbar-minimal"
                    showLineNumbers
                  >
                    <MarkdownPreviewToolbar>
                      <Button
                        aria-label="Copy full path"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        disabled={!selectedFile}
                        onClick={() => void handleCopyPath()}
                        size="icon"
                        title="Copy full path"
                        type="button"
                        variant="ghost"
                      >
                        <LinkIcon size={16} />
                      </Button>
                      <CodeBlockCopyButton
                        aria-label="Copy file contents"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        title="Copy file contents"
                      />
                      {onAttachFile ? (
                        <Button
                          aria-label="Attach to chat"
                          className="size-8 p-0 text-muted-foreground hover:text-foreground"
                          disabled={attaching || selectedContent === undefined}
                          onClick={() => void handleAttach()}
                          size="icon"
                          title="Attach to chat"
                          type="button"
                          variant="ghost"
                        >
                          {attaching ? <Spinner className="size-3.5" /> : <AttachFileIcon size={16} />}
                        </Button>
                      ) : null}
                      {onDownloadFile ? (
                        <Button
                          aria-label="Download file"
                          className="size-8 p-0 text-muted-foreground hover:text-foreground"
                          disabled={downloading || selectedContent === undefined}
                          onClick={() => void handleDownload()}
                          size="icon"
                          title="Download file"
                          type="button"
                          variant="ghost"
                        >
                          {downloading ? <Spinner className="size-3.5" /> : <DownloadIcon size={16} />}
                        </Button>
                      ) : null}
                      <Button
                        aria-label="Expand code preview"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setPreviewExpanded(true)}
                        size="icon"
                        title="Expand code preview"
                        type="button"
                        variant="ghost"
                      >
                        <Maximize2Icon size={16} />
                      </Button>
                      <Button
                        aria-label="Close preview"
                        className="size-8 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setSelectedPath(undefined)}
                        size="icon"
                        title="Close preview"
                        type="button"
                        variant="ghost"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </MarkdownPreviewToolbar>
                  </CodeBlock>
                </div>
                <Dialog open={previewExpanded} onOpenChange={setPreviewExpanded}>
                  <DialogContent className="flex h-[90dvh] max-w-5xl! flex-col gap-0 overflow-hidden p-0">
                    <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
                      <DialogTitle className="font-mono text-sm">
                        {selectedDir}{selectedName}
                        <span className="ml-2 font-sans text-xs font-normal text-muted-foreground">
                          {formatBytes(selectedFile?.size)}
                        </span>
                      </DialogTitle>
                    </DialogHeader>
                    <CodeBlock
                      className="flex min-h-0 flex-1 flex-col rounded-none border-0 bg-transparent"
                      code={selectedContent ?? ""}
                      contentClassName="text-xs [&_code]:text-xs"
                      language={languageForPath(selectedPath)}
                      scrollClassName="min-h-0 min-w-0 flex-1 overflow-auto scrollbar-minimal"
                      showLineNumbers
                    >
                      <CodeBlockCopyButton
                        aria-label="Copy file contents"
                        className="absolute top-2 right-2 z-10 size-7 rounded-full bg-background/80 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                        title="Copy file contents"
                      />
                    </CodeBlock>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}


// ── Resizable side panel ─────────────────────────────────────────────────────

/**
 * The sandbox files panel as a resizable right-hand column, opened from the
 * chat's top-right file icon. Same drag-handle recipe as chat-view and the
 * project file preview.
 */
export function ContainerFilesSidePanel({
  onClose,
  className,
  ...panelProps
}: Omit<ContainerFilesPanelProps, "onSelectedPathChange"> & { onClose: () => void }) {
  const [width, setWidth] = useState(TREE_ONLY_WIDTH);
  const [hasPreview, setHasPreview] = useState(false);
  const hasPreviewRef = useRef(false);

  // Opening the first file adds a whole column, so make room for it once. A
  // later drag is the user's, and closing the file leaves their width alone.
  const handleSelectedPathChange = useCallback((path: string | undefined) => {
    const next = Boolean(path);
    const justOpened = next && !hasPreviewRef.current;
    hasPreviewRef.current = next;
    setHasPreview(next);
    if (!justOpened) return;
    setWidth((current) =>
      Math.max(current, Math.min(WITH_PREVIEW_WIDTH, window.innerWidth * 0.75))
    );
  }, []);

  // Dragging past this would squeeze the preview to a few pixels beside the
  // fixed-width tree, so the floor rises while a file is open.
  const minWidth = hasPreview ? MIN_WIDTH_WITH_PREVIEW : MIN_WIDTH;

  return (
    <>
      {/* Drag handle */}
      <div
        aria-label="Resize sandbox files panel"
        role="separator"
        className="group relative z-10 hidden w-px shrink-0 cursor-col-resize select-none md:block"
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const startWidth = width;

          const overlay = document.createElement("div");
          overlay.style.cssText = "position:fixed;inset:0;z-index:9999;cursor:col-resize;";
          document.body.appendChild(overlay);

          const onMove = (mv: MouseEvent) => {
            const delta = startX - mv.clientX;
            setWidth(
              Math.max(minWidth, Math.min(window.innerWidth * 0.75, startWidth + delta))
            );
          };
          const onUp = () => {
            overlay.remove();
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
          };
          window.addEventListener("mousemove", onMove);
          window.addEventListener("mouseup", onUp);
        }}
      >
        <div className="absolute inset-y-0 -left-2 -right-2" />
      </div>
      {/* Below md the chat has no room to share, so the panel takes the view. */}
      <div
        className="m-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm md:w-(--container-files-width) md:flex-none"
        style={{ "--container-files-width": `${width}px` } as CSSProperties}
      >
        <ContainerFilesPanel
          {...panelProps}
          onClose={onClose}
          onSelectedPathChange={handleSelectedPathChange}
          className={className}
        />
      </div>
    </>
  );
}
